import moment from "moment-timezone";
import {
  FacilityClosureDisplayInfo,
  FacilityClosureForResourceAndZoneLookup,
  FacilityPolicyClosureLookupFloorPlanDto,
  IEffectiveFacilityPolicyFloorPlanDto,
  MONTH_DAY_YEAR,
} from "types";

/**
 * Builds a closure lookup from facility policies for O(1) access.
 * Single pass through policies to build Sets for quick checks and Maps for policy details.
 */
export function buildFacilityClosureLookupForResourceOrZone(
  policies: IEffectiveFacilityPolicyFloorPlanDto[] | null | undefined,
): FacilityPolicyClosureLookupFloorPlanDto {
  const resourceClosures = new Set<string>();
  const zoneClosures = new Set<string>();
  const hiddenResources = new Set<string>();
  const hiddenZones = new Set<string>();
  const policyByResourceUuid = new Map<string, IEffectiveFacilityPolicyFloorPlanDto>();
  const policyByZoneUuid = new Map<string, IEffectiveFacilityPolicyFloorPlanDto>();

  if (policies?.length) {
    for (const policy of policies) {
      if (policy.resourceUuid) {
        resourceClosures.add(policy.resourceUuid);
        policyByResourceUuid.set(policy.resourceUuid, policy);
        if (policy.hide) {
          hiddenResources.add(policy.resourceUuid);
        }
      } else if (policy.zoneUuid) {
        zoneClosures.add(policy.zoneUuid);
        policyByZoneUuid.set(policy.zoneUuid, policy);
        if (policy.hide) {
          hiddenZones.add(policy.zoneUuid);
        }
      }
    }
  }

  return {
    resourceClosures,
    zoneClosures,
    hiddenResources,
    hiddenZones,
    policyByResourceUuid,
    policyByZoneUuid,
  };
}

/**
 * Finds the applicable closure info for a resource or zone using O(1) Map lookup.
 * Prioritizes resource-level policies over zone-level policies.
 */
export function findFacilityClosureForResourceOrZone(
  closureLookup: FacilityClosureForResourceAndZoneLookup | undefined,
  resourceUuid: string | null,
  zoneUuid: string,
  timezone: string,
): FacilityClosureDisplayInfo | undefined {
  if (!closureLookup) {
    return undefined;
  }

  const { policyByResourceUuid, policyByZoneUuid } = closureLookup;

  // First check for resource-level policy (more specific)
  let matchingPolicy = resourceUuid ? policyByResourceUuid.get(resourceUuid) : undefined;

  // If no resource-level policy, check for zone-level policy
  if (!matchingPolicy) {
    matchingPolicy = policyByZoneUuid.get(zoneUuid);
  }

  if (!matchingPolicy) {
    return undefined;
  }

  // Format dates in office timezone
  const dateFormat = MONTH_DAY_YEAR;
  const startDate = moment(matchingPolicy.startTime).tz(timezone).format(dateFormat);
  const endDate = moment(matchingPolicy.endTime).tz(timezone).format(dateFormat);

  return {
    startDate,
    endDate,
    reason: matchingPolicy.reason || "",
  };
}
